import React, { useState } from 'react';
import {
  Typography,
  Stack,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import { useQuery } from 'react-query';

import { MainLayout } from '../components/layout/MainLayout';
import { appealsAPI } from '../services/api';
import { useToast } from '../components/common/ToastProvider';

const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

export const AppealsPage: React.FC = () => {
  const { showToast } = useToast();

  const [page, setPage] = useState(0);
  const rowsPerPage = 20;

  const getAppealsQuery = useQuery(
    ['appealsAPI.getAppeals', page],
    () => appealsAPI.getAppeals({ page }),
    {
      onError: (err) => {
        console.error(err);
        showToast('Failed to load appeals', 'error');
      },
    }
  );

  const renderContent = () => {
    if (getAppealsQuery.isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    const appeals = getAppealsQuery.data?.[0] ?? [];
    const total = getAppealsQuery.data?.[1] ?? 0;

    if (appeals.length === 0) {
      return (
        <Typography color='text.secondary'>No appeals available.</Typography>
      );
    }

    return (
      <>
        <TableContainer component={Paper} variant='outlined'>
          <Table size='small'>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell>
                  <Typography variant='subtitle2'>First Name</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2'>Last Name</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2'>Email</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2'>Phone Number</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2'>Message</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2'>Consent</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2'>Created At</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appeals.map((appeal) => (
                <TableRow key={appeal.id} hover>
                  <TableCell>{appeal.firstName}</TableCell>
                  <TableCell>{appeal.lastName}</TableCell>
                  <TableCell>{appeal.email}</TableCell>
                  <TableCell>{appeal.phoneNumber ?? '-'}</TableCell>
                  <TableCell>{appeal.message ?? '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={appeal.contactConsent ? 'Yes' : 'No'}
                      size='small'
                      color={appeal.contactConsent ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{formatDate(appeal.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component='div'
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[]}
          showFirstButton
          showLastButton
        />
      </>
    );
  };

  return (
    <MainLayout>
      <Stack spacing={4} width='100%'>
        <Typography variant='h4' gutterBottom>
          Appeals
        </Typography>
        {renderContent()}
      </Stack>
    </MainLayout>
  );
};
